import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import moment from "moment";
import { z } from "zod";
import { taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { createUUID } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { REASON_TYPES, ROUTE_NAMES } from "./constants";

const getReasonsUUID = createUUID();
const updateReasonUUID = createUUID();

const schema = z.object({
  holdReasons: z
    .array(
      z.object({
        value: z
          .string()
          .min(3, "Reason must be at least 3 characters long")
          .max(100, "Reason must not exceed 100 characters")
          .trim(),
      })
    )
    .superRefine((items, ctx) => {
      const uniqueValues = new Map();
      items.forEach((item, index) => {
        const firstIndex = uniqueValues.get(item.value);
        if (firstIndex !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Reason must be unique`,
            path: [index, "value"],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Reason must be unique`,
            path: [firstIndex, "value"],
          });
          return;
        }
        uniqueValues.set(item.value, index);
      });
    }),
  rescheduleReasons: z
    .array(
      z.object({
        value: z
          .string()
          .min(3, "Reason must be at least 3 characters long")
          .max(100, "Reason must not exceed 100 characters")
          .trim(),
        id: z.string().optional(),
        color: z.string().optional(),
        requireReapproval: z.boolean().optional(),
      })
    )
    .superRefine((items, ctx) => {
      const uniqueValues = new Map();
      items.forEach((item, index) => {
        const firstIndex = uniqueValues.get(item.value);
        if (firstIndex !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Reason must be unique`,
            path: [index, "value"],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Reason must be unique`,
            path: [firstIndex, "value"],
          });
          return;
        }
        uniqueValues.set(item.value, index);
      });
    }),
});

export function useManageOrderReasonsViewModel() {
  const stateManager = useComponentState();
  const [fetchedReasons, setFetchedReasons] = useState({
    holdReasons: [],
    rescheduleReasons: [],
  });

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      holdReasons: [],
      rescheduleReasons: [],
    },
    resolver: zodResolver(schema),
  });
  const holdReasonsForm = useFieldArray({
    control: methods.control,
    name: "holdReasons",
  });

  const rescheduleReasonsForm = useFieldArray({
    control: methods.control,
    name: "rescheduleReasons",
    keyName: "_id",
  });

  const getHoldReasons = () => {
    stateManager.abort(getReasonsUUID);
    stateManager.run(async () => {
      const response = await taskService.orderReasons.getHoldReasons();
      if (response.status === HTTP_STATUSES.OK) {
        const formattedData = response.data?.labels.map((reason) => ({
          value: reason,
        }));
        setFetchedReasons((prev) => ({
          ...prev,
          holdReasons: formattedData,
        }));
        methods.reset({
          ...methods.getValues(),
          holdReasons: formattedData,
        });
      }
    }, getReasonsUUID);
  };

  const getRescheduleReasons = () => {
    stateManager.abort(getReasonsUUID);
    stateManager.run(async () => {
      const response = await taskService.orderReasons.getRescheduleReasons();
      if (response.status === HTTP_STATUSES.OK) {
        const formattedData =
          response.data?.map((reason) => ({
            id: reason.id,
            value: reason.label,
            color: reason.color,
            requireReapproval: reason.requireReapproval,
          })) || [];
        setFetchedReasons((prev) => ({
          ...prev,
          rescheduleReasons: formattedData,
        }));
        methods.reset({
          ...methods.getValues(),
          rescheduleReasons: formattedData,
        });
      }
    }, getReasonsUUID);
  };

  const onSubmit = async (data, activeTab) => {
    if (activeTab === ROUTE_NAMES.HOLD_REASONS) {
      const holdReasons = {
        labels: data[REASON_TYPES.HOLD].map((item) => item.value),
      };
      await updateHoldReasons(holdReasons);
    } else if (activeTab === ROUTE_NAMES.RESCHEDULE_REASONS) {
      const rescheduleReasons = data[REASON_TYPES.RESCHEDULE].map((item) => ({
        id: item.id || null,
        label: item.value,
        color: item.color || null,
        requireReapproval: item.requireReapproval || false,
      }));
      await updateRescheduleReasons(rescheduleReasons);
    }
  };

  const updateHoldReasons = async (data) => {
    stateManager.abort(updateReasonUUID);
    return stateManager.run(async () => {
      const response = await taskService.orderReasons.updateHoldReasons({
        data,
        modifiedOn: moment().toISOString(),
        toastMessageConfig: {
          success: {
            text: "Hold reasons updated successfully",
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        getHoldReasons();
      }
      return response;
    }, updateReasonUUID);
  };

  const updateRescheduleReasons = async (data) => {
    stateManager.abort(updateReasonUUID);
    return stateManager.run(async () => {
      const response = await taskService.orderReasons.updateRescheduleReasons({
        data,
        modifiedOn: moment().toISOString(),
        toastMessageConfig: {
          success: {
            text: "Reschedule reasons updated successfully",
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        getRescheduleReasons();
      }
      return response;
    }, updateReasonUUID);
  };

  const handleReset = () => {
    methods.reset(fetchedReasons);
  };

  return {
    getHoldReasons,
    getRescheduleReasons,
    isLoading: stateManager.isBusy(),
    methods,
    holdReasonsForm,
    rescheduleReasonsForm,
    onSubmit,
    handleReset,
  };
}
